<template>
  <!-- [sc-48497 / task 48527] Una striscia sola, sopra tutto: la scadenza
       riguarda l'azienda intera, non la schermata in cui ci si trova. -->
  <div
    v-if="visible"
    class="trial-banner"
    :class="{ expired: readOnly, urgent: urgent }"
  >
    <!-- [sc-48583 / task 48585] Il messaggio arriva a pezzi, non come una
         stringa sola: le parole che contano - il nome dell'azienda, quanto
         manca, la data della cancellazione - vanno in grassetto, e un testo
         piatto non lo permette. A pezzi invece che interpretando del markup,
         perche' il nome dell'azienda lo scrive il cliente: qui non c'e'
         niente da ripulire, perche' non si interpreta niente. -->
    <span class="trial-text">
      <template v-for="(pezzo, i) in messaggio" :key="i"
        ><strong v-if="pezzo.strong">{{ pezzo.text }}</strong
        ><template v-else>{{ pezzo.text }}</template></template
      >
    </span>
    <a v-if="salesLink" class="trial-cta" :href="salesLink">
      {{ ctaLabel }}
    </a>
    <!-- [sc-46314] La terza strada, al decimo giorno: parlarne con noi. Sta
         in mezzo perche' e' la via di mezzo fra comprare e andarsene, e
         compare solo a prova finita: durante la prova la striscia deve
         spingere a provare il gestionale, non a interrompere il lavoro. -->
    <a
      v-if="callLink"
      class="trial-link"
      :href="callLink"
      target="_blank"
      rel="noopener"
    >
      <i class="fa-solid fa-calendar-check"></i>
      Prenota una call
    </a>
    <!-- [sc-48497 / task 48575] A prova finita la domanda successiva e'
         sempre la stessa: «e i miei dati?». La risposta sta a un clic, e
         solo l'amministratore puo' scaricarli. -->
    <router-link v-if="showExport" class="trial-link" to="/home/dati">
      <i class="fa-solid fa-download"></i>
      Scarica i tuoi dati
    </router-link>
  </div>
</template>

<script>
import { COMPANY_STATUS, PLAN_START, TRIAL_URGENT_DAYS } from "@/config/plans";
import { linkPrenotaCall, mailtoAttivazione } from "@/utilities/abbonamento";

/** Un pezzo di messaggio in grassetto. */
function forte(text) {
  return { text, strong: true };
}

/** Un pezzo di messaggio normale. */
function piano(text) {
  return { text, strong: false };
}

export default {
  name: "TrialBanner",
  computed: {
    trial() {
      return this.$store.getters["company/trial"];
    },
    readOnly() {
      return Boolean(this.trial?.readOnly);
    },
    /**
     * [sc-48497 / task 48528] Gli ultimi due giorni cambiano tono.
     *
     * La soglia e' la stessa del secondo preavviso via email: il banner e il
     * messaggio devono dire la stessa cosa nello stesso giorno, altrimenti chi
     * riceve l'email torna nel gestionale e ci trova scritto che c'e' tempo.
     */
    urgent() {
      if (this.readOnly) return false;
      const giorni = this.trial?.daysLeft;
      return typeof giorni === "number" && giorni <= TRIAL_URGENT_DAYS;
    },
    planName() {
      return PLAN_START.name;
    },
    piani() {
      // [sc-48583 / task 48585] «CRM START», non «START»: e' il nome con cui
      // il prodotto viene venduto, ed e' quello che il cliente ritrova
      // nell'email e nel listino. Solo START: PLUS non si prova, si vende
      // dopo una call, e il pulsante qui accanto ne chiede uno solo.
      return `CRM ${PLAN_START.name}`;
    },
    /**
     * Il nome dell'azienda, per chiamarla per nome.
     *
     * [sc-48583 / task 48585] Arriva dal marchio, che lo store tiene gia' per
     * l'intestazione: vuoto finche' non e' arrivato, e le frasi si scrivono
     * senza. Un banner che dice «La prova di scade tra 3 giorni» e' peggio di
     * uno che non nomina nessuno.
     */
    companyName() {
      const nome = this.$store.getters["company/branding"]?.name;
      return typeof nome === "string" ? nome.trim() : "";
    },
    visible() {
      // Un'azienda che ha convertito non ha piu' niente da leggere qui, e chi
      // non e' collegato non ha un'azienda di cui parlare.
      if (!this.$store.state.currentUser) return false;
      return (
        this.trial?.status === COMPANY_STATUS.TRIAL ||
        this.trial?.status === COMPANY_STATUS.EXPIRED
      );
    },
    /**
     * [sc-48497 / task 48532] La data in cui i dati spariscono, in italiano.
     *
     * Arriva dall'API gia' decisa; qui si formatta e basta. Vuota finche' la
     * prova e' in corso: e' un'informazione che riguarda solo chi ha smesso.
     */
    deletionLabel() {
      const quando = this.trial?.deletionAt;
      if (!quando) return "";
      const data = new Date(quando);
      if (Number.isNaN(data.getTime())) return "";
      return data.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    /**
     * Il messaggio, a pezzi, con le parole che contano in grassetto.
     *
     * [sc-48583 / task 48585] I testi vengono dal commento 48584 sulla story.
     * L'indirizzo a cui scrivere non e' ripetuto qui: sta nel pulsante
     * accanto, che apre l'email gia' scritta.
     */
    messaggio() {
      const nome = this.companyName;

      if (this.readOnly) {
        const parti = [
          piano("🔒 "),
          forte("Prova terminata."),
          piano(
            " I tuoi dati sono ancora al sicuro e puoi continuare a" +
              " consultarli. Per tornare a creare preventivi, gestire ordini," +
              " clienti e magazzino, attiva un abbonamento "
          ),
          forte(this.piani),
          piano("."),
        ];
        // La cancellazione non si annuncia solo per email: chi entra e trova
        // il gestionale bloccato deve leggere qui, dove sta guardando, che i
        // dati non restano per sempre.
        if (this.deletionLabel) {
          parti.push(
            piano(" I dati verranno cancellati il "),
            forte(this.deletionLabel),
            piano(".")
          );
        }
        return parti;
      }

      const giorni = this.trial?.daysLeft;

      // L'ultimo giorno si dice per esteso che si lavora ancora: «scadenza
      // oggi» si legge volentieri come «già scaduta», e chi lo capisce al
      // rovescio smette di lavorare un giorno prima del necessario.
      if (giorni === 0) {
        return [
          piano("⏳ "),
          forte("Ultimo giorno di prova"),
          piano(nome ? ` per ${nome}: ` : ": "),
          piano(
            "fino a stanotte puoi ancora lavorare. Da domani i tuoi dati" +
              " resteranno disponibili, ma non potrai più modificarli."
          ),
        ];
      }

      const quando = giorni === 1 ? "domani" : `tra ${giorni} giorni`;
      return [
        piano("⏳ La prova "),
        ...(nome ? [piano("di "), forte(nome), piano(" ")] : []),
        piano("scade "),
        forte(quando),
        // La conseguenza sta accanto al conto alla rovescia: un numero senza
        // cosa succede quando arriva a zero non fa decidere nessuno.
        piano(
          ". Continua a lavorare senza interruzioni: alla scadenza i tuoi dati" +
            " resteranno disponibili, ma non potrai più modificarli."
        ),
      ];
    },
    /**
     * Cosa c'e' scritto sul pulsante.
     *
     * [sc-48583 / task 48585] Durante la prova invita a passare al piano; a
     * prova finita chiede di riaccendere quello che si e' spento. Due momenti
     * diversi, due verbi diversi.
     */
    ctaLabel() {
      // [revisione 48654] «Entra in CRM START» sembrava una porta, non un
      // acquisto: si e' gia' dentro, si sta provando. Il verbo dice cosa
      // succede premendo - si attiva il piano - e il mailto ci sta dietro.
      return this.readOnly
        ? "⚡ Riattiva il tuo CRM"
        : `🚀 Attiva il piano CRM ${this.planName}`;
    },
    showExport() {
      // Solo a prova finita: durante la prova il banner deve spingere a
      // convertire, non a portare via i dati.
      return this.readOnly && this.$store.state.currentUser?.isAdmin === true;
    },
    /**
     * [sc-46314] L'agenda commerciale, e solo a prova finita.
     *
     * Vuota se la variabile non c'e': un pulsante che non porta da nessuna
     * parte e' peggio del pulsante assente.
     */
    callLink() {
      return this.readOnly ? linkPrenotaCall() : "";
    },
    salesLink() {
      // [sc-48583 / task 48586] L'indirizzo, l'oggetto e il corpo stanno in un
      // punto solo: la modale di fine prova manda la stessa richiesta.
      return mailtoAttivazione(this.companyName);
    },
  },
};
</script>

<style scoped>
.trial-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 16px;
  background: rgba(var(--brand-rgb, 0, 119, 168), 0.12);
  color: #333;
  font-size: 14px;
}

.trial-banner.urgent {
  background: #fff4e0;
  color: #8a5a00;
}

.trial-banner.urgent .trial-cta {
  background: #8a5a00;
}

.trial-banner.expired {
  background: #fdecea;
  color: #8a1c1c;
}

.trial-cta {
  padding: 6px 14px;
  border-radius: 4px;
  background: var(--brand, #0077a8);
  color: var(--brand-ink, #fff);
  text-decoration: none;
  white-space: nowrap;
}

/* [sc-48583] Stessa forma del «Scarica i dati» della modale di fine prova:
   bordo, fondo tenue e icona. Era un collegamento sottolineato, e accanto a
   un pulsante pieno si leggeva come una nota a margine. I colori li prende
   dal banner - qui il fondo è rosso, e il blu del marchio in mezzo stonerebbe
   - ma la forma è quella. */
.trial-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border: 0.5px solid currentColor;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.55);
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.trial-banner.expired .trial-cta {
  background: #8a1c1c;
}
</style>
